"use client";

import React from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS, useGlobalModalStack } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { Edit2 } from "lucide-react";
import type { ListEditorModalData } from "@/lib/modals/data/modal-data-types";
import { useListFieldEditor } from "./useListFieldEditor";
import { ListEditorContent } from "./ListEditorContent";
import type { ListFieldEditorProps } from "./types";

/**
 * List Field Editor Component
 *
 * Opens in a modal via the global modal stack. Handles display, editing,
 * and validation of comma-separated list items.
 * Stores as array in database, displays as comma-separated in UI.
 */
export function ListFieldEditor({
  value,
  fieldId,
  fieldLabel = fieldId,
  onSave,
  readOnlyMode = false,
  globalModalId,
  placeholder = "Enter item and press Add",
  maxItems,
}: ListFieldEditorProps) {
  // Map fieldId to the appropriate MODAL_IDS constant
  const fieldMap: Record<string, string> = {
    "contacts-tags": MODAL_IDS.listEditors.contactsTags,
    "companies-tags": MODAL_IDS.listEditors.companiesTags,
    "tickets-tags": MODAL_IDS.listEditors.ticketsTags,
    "tasks-tags": MODAL_IDS.listEditors.tasksTags,
    "time_entries-tags": MODAL_IDS.listEditors.timeEntriesTags,
    "employees-tags": MODAL_IDS.listEditors.employeesTags,
    "communications-tags": MODAL_IDS.listEditors.communicationsTags,
    "deals-tags": MODAL_IDS.listEditors.dealsTags,
  };
  const modalId = fieldMap[fieldId] ?? globalModalId ?? `list-editor-${fieldId}`;

  const globalModalStack = useGlobalModalStack();

  const editor = useListFieldEditor({
    value,
    fieldLabel,
    onSave,
    readOnlyMode,
    maxItems,
  });

  return (
    <ModalContainer<ListEditorModalData> modalId={modalId}>
      {({ isOpen, onClose }) => (
        <>
          {/* Trigger Button */}
          {!isOpen && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title={`Edit ${fieldLabel}`}
              onClick={() => {
                editor.initializeEditor();
                globalModalStack.openModal(modalId);
              }}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          )}

          {/* Modal */}
          {isOpen && (
            <ResponsiveModal
              globalModalId={modalId}
              open={isOpen}
              onOpenChange={(open) => {
                if (!open) {
                  editor.handleCancel();
                  onClose();
                }
              }}
              title={`Edit ${fieldLabel}`}
              description={`Manage ${fieldLabel.toLowerCase()}`}
              maxWidth="md"
              showCloseButton={true}
            >
              <ListEditorContent
                items={editor.items}
                newItem={editor.newItem}
                validationErrors={editor.validationErrors}
                editingIndex={editor.editingIndex}
                editingValue={editor.editingValue}
                isSaving={editor.isSaving}
                placeholder={placeholder}
                maxItems={maxItems}
                fieldLabel={fieldLabel}
                onNewItemChange={editor.setNewItem}
                onAddItem={editor.handleAddItem}
                onRemoveItem={editor.handleRemoveItem}
                onStartEdit={editor.handleStartEdit}
                onSaveEdit={editor.handleSaveEdit}
                onCancelEdit={editor.handleCancelEdit}
                onEditingValueChange={editor.setEditingValue}
                onKeyDown={editor.handleKeyDown}
                onSave={editor.handleSave}
                onCancel={editor.handleCancel}
                onClose={onClose}
              />
            </ResponsiveModal>
          )}
        </>
      )}
    </ModalContainer>
  );
}
